"use client";

import Image from "next/image";
import Link from "next/link";
import { Suspense, useState, type FormEvent } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import AgentSignup from "@/components/agent-signup";
import Footer from "@/components/footer";
import TurnstileChallenge from "@/components/turnstile-challenge";
import { buildAuthFlowRedirectUrl, safeAccountDestination, API_KEY_DESTINATION } from "@/lib/auth/redirect-target";
import { getAuthErrorMessage } from "@/lib/auth/error-message";
import { normalizeDisplayName } from "@/lib/display-name";
import {
  mergeReadmeReferralMetadata,
  readReadmeReferralAttributionFromCookieString,
  readLastTouchFromCookieString,
} from "@/lib/marketing/readme-referral-attribution";
import { captureProductEvent } from "@/lib/posthog/product-events";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";
import { buildPublicSiteUrl } from "@/lib/site-url";

const providerButtons = [
  {
    id: "google",
    label: "Google",
    icon: "/icons/oauth/google.svg",
  },
  {
    id: "github",
    label: "GitHub",
    icon: "/icons/oauth/github.svg",
  },
] as const;

type OAuthProvider = (typeof providerButtons)[number]["id"];

type SignUpPageShellProps = {
  showAiSignup: boolean;
  destination?: string;
};

function SignUpPageShell({ showAiSignup, destination = API_KEY_DESTINATION }: SignUpPageShellProps) {
  const supabase = getSupabaseBrowserClient();
  const router = useRouter();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [magicName, setMagicName] = useState("");
  const [magicEmail, setMagicEmail] = useState("");
  const [status, setStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeAction, setActiveAction] = useState<
    "anonymous" | "password" | "magic" | "oauth" | null
  >(null);
  const [anonymousCaptchaToken, setAnonymousCaptchaToken] = useState<string | null>(null);
  const [passwordCaptchaToken, setPasswordCaptchaToken] = useState<string | null>(null);
  const [magicCaptchaToken, setMagicCaptchaToken] = useState<string | null>(null);
  const [anonymousCaptchaResetKey, setAnonymousCaptchaResetKey] = useState(0);
  const [passwordCaptchaResetKey, setPasswordCaptchaResetKey] = useState(0);
  const [magicCaptchaResetKey, setMagicCaptchaResetKey] = useState(0);

  const buildSignupMetadata = (baseMetadata: Record<string, unknown>) =>
    mergeReadmeReferralMetadata(
      {...baseMetadata, ...(readLastTouchFromCookieString(document.cookie)?{marketing_last_touch:readLastTouchFromCookieString(document.cookie)}:{})},
      readReadmeReferralAttributionFromCookieString(document.cookie),
    );

  const handleAnonymousSignUp = async () => {
    if (!anonymousCaptchaToken) {
      setErrorMessage("Complete human verification before continuing.");
      return;
    }
    setActiveAction("anonymous");
    setErrorMessage(null);
    setStatus(null);
    captureProductEvent("signup_started", {
      method: "anonymous",
      surface: "website",
    });

    try {
      const { error } = await supabase.auth.signInAnonymously({
        options: {
          captchaToken: anonymousCaptchaToken,
          data: buildSignupMetadata({
            signup_source: "ai_agent_marketing_page",
          }),
        },
      });

      if (error) {
        setErrorMessage(getAuthErrorMessage(error));
        return;
      }

      setStatus("Anonymous session created. Redirecting...");
      router.push(destination);
    } catch {
      setErrorMessage("We could not connect. Check your connection and try again. Your details are still here.");
      setStatus(null);
    } finally {
      setActiveAction(null);
      setAnonymousCaptchaToken(null);
      setAnonymousCaptchaResetKey((current) => current + 1);
    }
  };

  const handlePasswordSignUp = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!passwordCaptchaToken) {
      setErrorMessage("Complete human verification before continuing.");
      return;
    }
    setActiveAction("password");
    setErrorMessage(null);
    setStatus(null);
    captureProductEvent("signup_started", {
      method: "password",
      surface: "website",
    });

    try {
      const redirectTo = buildAuthFlowRedirectUrl(
        destination,
        "password_signup",
        window.location.origin,
      );
      const trimmedName = normalizeDisplayName(name);
      const signupMetadata = buildSignupMetadata(
        trimmedName ? { name: trimmedName } : {},
      );
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          captchaToken: passwordCaptchaToken,
          emailRedirectTo: redirectTo,
          data: Object.keys(signupMetadata).length > 0 ? signupMetadata : undefined,
        },
      });

      if (error) {
        setErrorMessage(getAuthErrorMessage(error));
        return;
      }

      if (data.session) {
        setStatus("Account created. Redirecting...");
        router.push(destination);
        return;
      }

      setStatus(`Check your inbox at ${email.trim()}. Confirm your email to open your guided first request. If the message is missing, check spam or sign in after confirming.`);
    } catch {
      setErrorMessage("We could not connect. Check your connection and try again. Your details are still here.");
      setStatus(null);
    } finally {
      setActiveAction(null);
      setPasswordCaptchaToken(null);
      setPasswordCaptchaResetKey((current) => current + 1);
    }
  };

  const handleMagicLink = async (
    event: FormEvent<HTMLFormElement>,
  ) => {
    event.preventDefault();
    if (!magicCaptchaToken) {
      setErrorMessage("Complete human verification before continuing.");
      return;
    }
    setActiveAction("magic");
    setErrorMessage(null);
    setStatus(null);
    captureProductEvent("signup_started", {
      method: "magic",
      surface: "website",
    });

    try {
      const redirectTo = buildAuthFlowRedirectUrl(
        destination,
        "magic_signup",
        window.location.origin,
      );
      const trimmedName = normalizeDisplayName(magicName);
      const signupMetadata = buildSignupMetadata(
        trimmedName ? { name: trimmedName } : {},
      );
      const { error } = await supabase.auth.signInWithOtp({
        email: magicEmail,
        options: {
          captchaToken: magicCaptchaToken,
          emailRedirectTo: redirectTo,
          shouldCreateUser: true,
          data: Object.keys(signupMetadata).length > 0 ? signupMetadata : undefined,
        },
      });

      if (error) {
        setErrorMessage(getAuthErrorMessage(error));
        return;
      }

      setStatus(`Check your inbox at ${magicEmail.trim()}. Open the link in this browser to continue to your first request. Check spam if it has not arrived; you can request another link below.`);
    } catch {
      setErrorMessage("We could not connect. Check your connection and try again. Your details are still here.");
      setStatus(null);
    } finally {
      setActiveAction(null);
      setMagicCaptchaToken(null);
      setMagicCaptchaResetKey((current) => current + 1);
    }
  };

  const handleOAuthSignIn = async (provider: OAuthProvider) => {
    setActiveAction("oauth");
    setErrorMessage(null);
    setStatus(`Opening ${provider === "google" ? "Google" : "GitHub"} securely...`);
    captureProductEvent("signup_started", {
      method: `oauth_${provider}`,
      surface: "website",
    });

    try {
      const redirectTo = buildPublicSiteUrl(
        `/auth/callback?next=${encodeURIComponent(destination)}&signup=1`,
        window.location.origin,
      );
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo,
        },
      });

      if (error) {
        setErrorMessage(getAuthErrorMessage(error));
        setStatus(null);
        setActiveAction(null);
      }
    } catch {
      setErrorMessage("We could not open your sign-in provider. Check your connection and try again.");
      setStatus(null);
      setActiveAction(null);
    }
  };

  return (
    <div className="min-h-screen text-[var(--text)]">
      <main className="relative min-h-screen">
        <div className="mx-auto w-full max-w-3xl px-6 pb-24 pt-12">
          <div className="mb-6 flex items-center justify-between gap-3">
            <Link
              href="/"
              className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold text-[var(--text)] transition hover:border-white/40"
            >
              Return home
            </Link>
            <Link
              href={`/signin?next=${encodeURIComponent(destination)}`}
              className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-semibold text-white/80 transition hover:border-white/40 hover:text-[var(--text)]"
            >
              Sign in
            </Link>
          </div>

          <section className="panel border border-white/15 p-6 sm:p-8">
            <div className="space-y-5">
              <div className="space-y-2">
                <p className="text-sm uppercase tracking-[0.28em] text-white/80">
                  deepface.dev access
                </p>
                <h1 className="font-[var(--font-display)] text-3xl leading-tight text-[var(--text)] sm:text-4xl">
                  Get your API key
                </h1>
                <p className="text-base leading-7 text-white/85">
                  50 free API calls every month. No card required.
                </p>
                <p className="max-w-xl text-sm leading-6 text-white/65">
                  Create an account, get your key, and try a sample.
                </p>
              </div>

              {(status || errorMessage) && (
                <div
                  className={`rounded-2xl border px-4 py-3 text-sm leading-6 ${
                    errorMessage
                      ? "border-red-400/40 text-red-200"
                      : "border-emerald-400/40 text-emerald-200"
                  }`}
                  role={errorMessage ? "alert" : "status"}
                >
                  {errorMessage ?? status}
                </div>
              )}
              <div className="space-y-3">
                {showAiSignup ? (
                  <div className="rounded-2xl border border-[var(--accent)]/35 bg-[var(--accent)]/10 p-5">
                    <p className="text-sm uppercase tracking-[0.2em] text-[var(--accent)]">
                      AI agent access
                    </p>
                    <h2 className="mt-2 font-[var(--font-display)] text-2xl text-[var(--text)]">
                      Start anonymously
                    </h2>
                    <p className="mt-3 text-sm leading-6 text-white/80">
                      Try 10 calls each month anonymously. Create a full account for 50 free calls each month.
                    </p>
                    <TurnstileChallenge
                      key={anonymousCaptchaResetKey}
                      action="signup_anonymous"
                      onTokenChange={setAnonymousCaptchaToken}
                    />
                    <button
                      type="button"
                      onClick={handleAnonymousSignUp}
                      disabled={activeAction !== null || !anonymousCaptchaToken}
                      className="mt-4 w-full rounded-xl bg-[var(--accent)] px-6 py-3 text-base font-semibold text-black transition hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      {activeAction === "anonymous"
                        ? "Starting anonymous session..."
                        : "Continue anonymously"}
                    </button>
                  </div>
                ) : null}
                <div className="grid grid-cols-1 gap-3 min-[360px]:grid-cols-2">
                  {providerButtons.map((provider) => (
                    <button
                      key={provider.id}
                      type="button"
                      onClick={() => handleOAuthSignIn(provider.id)}
                      disabled={activeAction !== null}
                      aria-label={`Continue with ${provider.label}`}
                      className="flex items-center justify-center gap-2 rounded-xl border border-white/30 bg-white/[0.12] px-3 py-2.5 sm:px-4 text-base font-semibold text-[var(--text)] transition hover:border-white/50 hover:bg-white/[0.16] disabled:cursor-not-allowed disabled:opacity-70"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-white/60 bg-white/95 shadow-[0_4px_12px_rgba(2,6,23,0.35)]">
                        <Image
                          src={provider.icon}
                          alt=""
                          width={24}
                          height={24}
                          className="h-5 w-5"
                        />
                      </span>
                      <span>{provider.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-white/70">
                <span className="h-px flex-1 bg-white/20" aria-hidden="true" />
                or
                <span className="h-px flex-1 bg-white/20" aria-hidden="true" />
              </div>

              <form
                method="post"
                aria-busy={activeAction === "password"}
                className="space-y-4 rounded-2xl border border-white/20 bg-white/[0.08] p-5"
                onSubmit={handlePasswordSignUp}
              >
                <label className="flex flex-col gap-2 text-sm uppercase tracking-[0.2em] text-white/80">
                  Name
                  <input
                    type="text"
                    name="name"
                    placeholder="Your name"
                    autoComplete="name"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    className="rounded-xl border border-white/25 bg-[#0f1826] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none transition focus:border-[var(--accent)]"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm uppercase tracking-[0.2em] text-white/80">
                  Email
                  <input
                    type="email"
                    name="email"
                    placeholder="you@company.com"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    required
                    className="rounded-xl border border-white/25 bg-[#0f1826] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none transition focus:border-[var(--accent)]"
                  />
                </label>
                <label className="flex flex-col gap-2 text-sm uppercase tracking-[0.2em] text-white/80">
                  Password
                  <input
                    type="password"
                    name="password"
                    placeholder="Create a password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    required
                    className="rounded-xl border border-white/25 bg-[#0f1826] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none transition focus:border-[var(--accent)]"
                  />
                </label>
                <button
                  type="submit"
                  disabled={activeAction !== null || !passwordCaptchaToken}
                  className="w-full rounded-xl bg-[var(--accent)] px-6 py-3 text-base font-semibold text-black transition hover:shadow-glow disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {activeAction === "password"
                    ? "Creating account..."
                    : "Create account"}
                </button>
                <TurnstileChallenge
                  key={passwordCaptchaResetKey}
                  action="signup_password"
                  onTokenChange={setPasswordCaptchaToken}
                />
                <p className="text-sm text-white/75">
                  By continuing, you agree to our{" "}
                  <Link
                    href="/terms"
                    className="text-[var(--accent)] hover:text-[var(--accent-2)]"
                  >
                    Terms of use
                  </Link>
                  .
                </p>
              </form>

              <div className="rounded-2xl border border-white/20 bg-white/[0.08] p-5">
                <form method="post" className="space-y-3" aria-busy={activeAction === "magic"} onSubmit={handleMagicLink}>
                  <p className="text-sm uppercase tracking-[0.2em] text-white/80">
                    Magic link
                  </p>
                  <label className="flex flex-col gap-2 text-sm uppercase tracking-[0.2em] text-white/80">
                    Name
                    <input
                      type="text"
                      name="magic-name"
                      placeholder="Your name"
                      autoComplete="name"
                      value={magicName}
                      onChange={(event) => setMagicName(event.target.value)}
                      className="rounded-xl border border-white/25 bg-[#0f1826] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none transition focus:border-[var(--accent)]"
                    />
                  </label>
                  <label className="flex flex-col gap-2 text-sm uppercase tracking-[0.2em] text-white/80">
                    Email
                    <input
                      type="email"
                      name="magic-email"
                      placeholder="you@company.com"
                      autoComplete="email"
                      value={magicEmail}
                      onChange={(event) => setMagicEmail(event.target.value)}
                      required
                      className="rounded-xl border border-white/25 bg-[#0f1826] px-4 py-3 text-base text-white placeholder:text-white/45 outline-none transition focus:border-[var(--accent)]"
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={activeAction !== null || !magicCaptchaToken}
                    className="w-full rounded-xl border border-white/30 bg-white/10 px-6 py-3 text-base font-semibold text-[var(--text)] transition hover:border-white/50 hover:bg-white/[0.14] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {activeAction === "magic"
                      ? "Sending link..."
                      : "Send magic link"}
                  </button>
                  <TurnstileChallenge
                    key={magicCaptchaResetKey}
                    action="signup_magic"
                    onTokenChange={setMagicCaptchaToken}
                  />
                </form>
              </div>

              <p className="text-sm text-white/75">
                Already have an account?{" "}
                <Link
                  href={`/signin?next=${encodeURIComponent(destination)}`}
                  className="text-[var(--accent)] hover:text-[var(--accent-2)]"
                >
                  Sign in
                </Link>
                .
              </p>
            </div>
          </section>
        </div>
      </main>

      <Footer showAuthCtas={false} />
    </div>
  );
}

function SignUpPageSearchParams() {
  const searchParams = useSearchParams();
  if (searchParams.get("agent") === "1" && searchParams.get("anonymous") !== "1") return <AgentSignup destination={safeAccountDestination(searchParams.get("next"), API_KEY_DESTINATION)} />;
  return <SignUpPageShell showAiSignup={searchParams.get("agent") === "1"} destination={safeAccountDestination(searchParams.get("next"), API_KEY_DESTINATION)} />;
}

export default function SignUpPage() {
  return (
    <Suspense fallback={<SignUpPageShell showAiSignup={false} />}>
      <SignUpPageSearchParams />
    </Suspense>
  );
}
